<template>
  <!-- DealerInventoryPanel — Panel 3: 딜러 재고 - REQ-FE-TRC-003~006 -->
  <!-- SPEC-CHINA-LOT-FLOW-DASHBOARD-001 REQ-FE-DETAIL-005,006,007: dailyTrend chart + 만료 badge + 확장 필드 -->
  <a-card class="lot-trace-panel" :loading="loading">
    <template #title>
      <span>{{ $t('lotTrace_panel_dealerInventory') }}</span>
      <a-badge
        :count="panelData.count"
        :overflow-count="999"
        :style="{ marginLeft: '8px', backgroundColor: panelData.count > 0 ? '#52c41a' : '#d9d9d9' }"
      />
      <span v-if="panelData.truncated" style="font-size: 12px; color: #faad14; margin-left: 8px;">
        {{ $t('lotTrace_truncated') }}
        ({{ $t('dealerGsp_total_count', { count: panelData.preCapTotal }) }})
      </span>
      <!-- REQ-FE-DETAIL-007: 만료 임박/만료 badge -->
      <a-tag
        v-if="daysUntilExpiry !== null && daysUntilExpiry !== undefined && daysUntilExpiry < 0"
        color="error"
        style="margin-left: 8px;"
        data-testid="expiry-badge-expired"
      >
        {{ $t('lotFlow_detail_expiredSince', { days: Math.abs(daysUntilExpiry) }) }}
      </a-tag>
      <a-tag
        v-else-if="daysUntilExpiry !== null && daysUntilExpiry !== undefined && daysUntilExpiry < 30"
        color="warning"
        style="margin-left: 8px;"
        data-testid="expiry-badge-expiring"
      >
        {{ $t('lotFlow_detail_expiringIn', { days: daysUntilExpiry }) }}
      </a-tag>
    </template>
    <template #extra>
      <!-- REQ-FE-TRC-006: currentQuantity 표시 -->
      <span v-if="panelData.currentQuantity !== undefined" style="margin-right: 12px; font-size: 13px; color: #595959;">
        {{ $t('lotTrace_col_currentQuantity') }}: <strong>{{ panelData.currentQuantity ?? '-' }}</strong>
      </span>
      <a-button v-if="value" type="link" size="small" @click="viewAll">{{ $t('lotTrace_actions_viewAll') }}</a-button>
    </template>

    <template v-if="loading">
      <a-skeleton active :paragraph="{ rows: 3 }" />
    </template>
    <template v-else-if="panelData.count === 0">
      <a-empty :description="emptyMessage" />
    </template>
    <template v-else>
      <!-- REQ-FE-DETAIL-005: 일별 변동 line chart -->
      <div v-if="dailyTrend && dailyTrend.length > 0" style="margin-bottom: 16px;">
        <inventory-daily-trend-chart
          :daily-trend="dailyTrend"
          :product-description="productDescription"
        />
      </div>

      <div style="overflow-x: auto;">
        <a-table
          :data-source="panelData.rows"
          :columns="columns"
          :pagination="false"
          row-key="_id"
          size="small"
          :scroll="{ x: 900 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'batchCode'">
              <a-button
                type="link"
                size="small"
                style="padding: 0; min-height: 44px; height: auto;"
                @click="deepLinkToSource(record.batchCode)"
              >{{ record.batchCode || '-' }}</a-button>
            </template>
            <template v-else-if="['snapshotDate', 'productionDate', 'expiredDate'].includes(column.key)">
              {{ formatDate(record[column.dataIndex]) }}
            </template>
            <!-- REQ-FE-DETAIL-006: 확장 필드 -->
            <template v-else-if="column.key === 'warehouseName'">
              {{ record.warehouseName || '-' }}
            </template>
          </template>
        </a-table>
      </div>
    </template>
  </a-card>
</template>

<script>
import InventoryDailyTrendChart from './InventoryDailyTrendChart.vue';

export default {
  name: 'DealerInventoryPanel',
  components: { InventoryDailyTrendChart },
  props: {
    panelData: {
      type: Object,
      default: () => ({ count: 0, preCapTotal: 0, truncated: false, rows: [], currentQuantity: 0, latestSnapshotDate: null }),
    },
    loading: { type: Boolean, default: false },
    value: { type: String, default: '' },
    anomalies: { type: Array, default: () => [] },
    // 확장 필드 (REQ-FE-DETAIL-005, 007)
    dailyTrend: { type: Array, default: () => [] },
    daysUntilExpiry: { type: Number, default: null },
    productDescription: { type: String, default: null },
  },
  emits: ['navigate'],
  computed: {
    columns() {
      return [
        { title: this.$t('lotTrace_col_batchCode'), dataIndex: 'batchCode', key: 'batchCode', width: 170 },
        { title: this.$t('lotTrace_col_snapshotDate'), dataIndex: 'snapshotDate', key: 'snapshotDate', width: 130 },
        { title: this.$t('lotTrace_col_productCode'), dataIndex: 'productCode', key: 'productCode', width: 130 },
        { title: this.$t('lotTrace_col_quantity'), dataIndex: 'quantity', key: 'quantity', width: 80, align: 'right' },
        // 확장 필드
        { title: this.$t('lotFlow_detail_field_warehouseName'), dataIndex: 'warehouseName', key: 'warehouseName', width: 120 },
        { title: this.$t('lotTrace_col_productionDate'), dataIndex: 'productionDate', key: 'productionDate', width: 120 },
        { title: this.$t('lotTrace_col_expiredDate'), dataIndex: 'expiredDate', key: 'expiredDate', width: 120 },
      ];
    },
    emptyMessage() {
      const match = (this.anomalies || []).find((a) =>
        ['not_found', 'missing_dealer_inventory'].includes(a.type)
      );
      if (match) {
        const key = match.type === 'not_found'
          ? 'lotTrace_anomaly_not_found'
          : 'lotTrace_anomaly_missing_dealer_inventory';
        return this.$t(key);
      }
      return this.$t('lotTrace_emptyState');
    },
  },
  methods: {
    formatDate(val) {
      if (!val) return '-';
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' });
    },
    viewAll() {
      if (!this.value) return;
      this.$emit('navigate', `/dealer-gsp/inventory-snapshots?batchCode=${encodeURIComponent(this.value)}`);
    },
    deepLinkToSource(batchCode) {
      if (!batchCode) return;
      this.$emit('navigate', `/dealer-gsp/inventory-snapshots?batchCode=${encodeURIComponent(batchCode)}`);
    },
  },
};
</script>
